import React, { useRef } from 'react'
import { Tooltip } from 'antd'
import { PiCheckBold } from 'react-icons/pi'
import IntlMessages from '../../util/IntlMessages'
import LinkButton from '../link-button'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'

interface FreemiumPricingTooltipProps {
  children: React.ReactNode
  isFreemiumPlan: boolean
  freemiumData: string[]
  coreData: string[]
  completeData: string[]
  title?: React.ReactNode
  featureName?: string
  featureCategory?: string
}

const FreemiumPricingTooltip: React.FC<FreemiumPricingTooltipProps> = ({ 
  children,
  isFreemiumPlan,
  freemiumData,
  coreData,
  completeData,
  title,
  featureName,
  featureCategory,
}) => {
  const hasTrackedView = useRef(false)

  if (!isFreemiumPlan) {
    return <>{children}</>
  }

  const handleTooltipVisibilityChange = (visible: boolean) => {
    if (visible && !hasTrackedView.current && featureName && featureCategory) {
      hasTrackedView.current = true
      track('FEATURE_GATE_VIEWED', {
        ...getAnalyticsProperties(),
        featureName,
        featureCategory,
        pagePath: window.location.pathname,
      })
    }
  }

  const handleUpgradeClick = () => {
    if (featureName && featureCategory) {
      track('UPGRADE_CTA_CLICKED', {
        ...getAnalyticsProperties(),
        featureName,
        featureCategory,
        pagePath: window.location.pathname,
      })
    }
  }

  return (
    <Tooltip
      placement="bottom"
      classNames={{ 
        root: 'freemium-pricing-tooltip',
      }}
      onOpenChange={handleTooltipVisibilityChange}
      title={
        <div className='freemium-pricing-tooltip-content'>
          {title && <div className='title'>{title}</div>}
          <div className='card freemium-plan'>
            <div className='card-header'><IntlMessages id="components.freemiumPricingTooltip.freemiumPlan" /></div>
            <div className='card-body'>
              {freemiumData.map((id) => (
                <div key={id}>
                  <PiCheckBold className='check' /> <IntlMessages id={id} />
                </div>
              ))}
            </div>
          </div>
          <div className='card core-plan'>
            <div className='card-header'>
              <IntlMessages 
                id="components.freemiumPricingTooltip.corePlan" 
                values={{ 
                  price: '2',
                  span: (chunk) => <span className="price">{chunk}</span>,
                }} 
              />
            </div>
            <div className='card-body'>
              {coreData.map((id) => (
                <div key={id}>
                  <PiCheckBold className='check' /> <IntlMessages id={id} />
                </div>
              ))}
            </div>
          </div>
          <div className='card complete-plan'>
            <div className='card-header'>
              <IntlMessages 
                id="components.freemiumPricingTooltip.completePlan" 
                values={{ 
                  price: '4',
                  span: (chunk) => <span className="price">{chunk}</span>,
                }}
              />
            </div>
            <div className='card-body'>
              {completeData.map((id) => (
                <div key={id}>
                  <PiCheckBold className='check' /> <IntlMessages id={id} />
                </div>
              ))}
            </div>
          </div>

          <div className='footer'>
            <LinkButton to={'/app/settings/billing'} block type="primary" size='large' onClick={handleUpgradeClick}>
              <IntlMessages id="components.freemiumPricingTooltip.upgradePlan" />
            </LinkButton>
          </div>
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}

export default FreemiumPricingTooltip
